// Rendered, Google-only auth front door — replaces the old LoginGate's blind
// redirect. Sign-in by default with a "Create an account" toggle to sign-up; both
// buttons fire the SAME Google OAuth (the only auth that exists — no email/password).
// Ports mockup section A cards 1 (sign in) & 2 (sign up).

import { useCallback, useState } from 'react';
import { supabase } from '../lib/auth';
import { track } from '../lib/analytics';
import { readBriefingTokenFromUrl, stashBriefingTokenForLogin } from '../lib/briefing-intent';
import { stashReturnRoute } from '../lib/auth-redirect';
import { TerumLogo } from './shared/TerumLogo';
import { GoogleLogo } from './shared/Icons';

export function AuthScreen() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');

  const signIn = useCallback(() => {
    track('auth_signin_clicked'); // funnel entry — both sign-in and sign-up fire the same Google OAuth
    // bug-177: the OAuth redirect drops the URL hash where ?b=<briefing token> lives,
    // so stash it first and re-reconcile after sign-in (popped in the AppLayout effect).
    const briefingToken = readBriefingTokenFromUrl();
    if (briefingToken) stashBriefingTokenForLogin(briefingToken);
    // Same reason, same fix, for the ROUTE: `redirectTo` below is origin + pathname, and this is a
    // HashRouter app, so the hash carrying the route never survives the round trip. A briefing's
    // "#/conversations/<id>" link therefore dropped the signed-out user on the default surface.
    stashReturnRoute(window.location.hash);
    void supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + window.location.pathname },
    });
  }, []);

  if (mode === 'signup') {
    return (
      <div className="auth-root">
        <div className="auth-card">
          <div className="auth-brand sm"><TerumLogo size={18} className="tlogo" />TERUM</div>
          <h2 className="auth-h">Create your account</h2>
          {/* Copy tracks .planning/POSITIONING.md — the 2026 team framing ("shared context layer"),
            * NOT the retired individual "portable AI memory layer" story. */}
          <p className="auth-sub">
            The shared context layer for your team. Terum watches the signal across the tools you already
            use and routes only what matters to each person.
          </p>
          <div className="auth-feat">
            <div className="r"><span className="ck">✓</span> Connect Slack, Google, GitHub, Notion &amp; more — one click each</div>
            <div className="r"><span className="ck">✓</span> A daily brief with only what matters to you</div>
          </div>
          <button className="auth-google" onClick={signIn}>
            <GoogleLogo size={18} />Sign up with Google
          </button>
          <div className="auth-fine">
            One click with Google creates your account — no password needed. By continuing you agree to Terum's
            Terms &amp; Privacy.
          </div>
          <div className="auth-swap">
            Already have an account? <a onClick={() => setMode('signin')}>Sign in</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-root">
      <div className="auth-card center">
        <div className="auth-brand hero"><TerumLogo size={28} className="tlogo" />TERUM</div>
        <h2 className="auth-h hero">Sign in to Terum</h2>
        <p className="auth-sub">The shared context layer for your team.</p>
        <button className="auth-google lg" onClick={signIn}>
          <GoogleLogo size={18} />Continue with Google
        </button>
        <div className="auth-trust">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="4" y="11" width="16" height="9" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          Private by default · one click, no password
        </div>
        <div className="auth-swap">
          New to Terum? <a onClick={() => setMode('signup')}>Create an account →</a>
        </div>
      </div>
    </div>
  );
}
